import { useEffect, type FC } from 'react';
import type { IndustryOnePagerConfig } from './copy';
import { OnePagerLogo } from './components/OnePagerLogo';
import { OnePagerHero } from './components/OnePagerHero';
import { OnePagerSection } from './components/OnePagerSection';
import { OnePagerIllustration } from './components/OnePagerIllustration';
import { BookACallButton } from './components/BookACallButton';
import { OnePagerFooter } from './components/OnePagerFooter';
import { OnePagerMenu } from './components/OnePagerMenu';

// Page-level composition for any industry's one-pager. The page is a
// standalone marketing surface — no Navbar by design — but it still
// participates in site-wide analytics and cookie consent (wired in App.tsx).
//
// Per-industry copy, illustration and PDF path come in via `config`; the
// layout, atomic components and behavior are shared across industries.
export const IndustryOnePagerPage: FC<{ config: IndustryOnePagerConfig }> = ({
  config,
}) => {
  // SPA navigation means the tab title and meta description otherwise
  // inherit the home page's values. Restore on unmount so navigating
  // away leaves the document in a clean state.
  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl?.getAttribute('content') ?? null;

    document.title = config.meta.title;
    if (descEl) descEl.setAttribute('content', config.meta.description);

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute('content', prevDesc);
    };
  }, [config.meta.title, config.meta.description]);

  return (
    <div className="min-h-screen bg-unimo-onepager-bg font-sans text-unimo-onepager-ink antialiased print:min-h-0">
      <div className="w-full overflow-x-hidden">
        <main className="mx-auto max-w-[960px] px-6 pt-6 md:px-12 md:pt-[60px]">
          <OnePagerLogo />
          <OnePagerHero hero={config.hero} />
          <div className="flex flex-col gap-7 md:gap-9 md:text-[17px] md:leading-[1.6]">
            {config.sections.map((section) => (
              <OnePagerSection key={section.heading} section={section} />
            ))}
          </div>
          {/* Inline CTA is print-only. On screen the floating variant
              below handles the call-to-action so it stays visible
              regardless of scroll position. */}
          <div className="mt-8 hidden print:flex md:mt-12">
            <BookACallButton variant="inline" analyticsSlug={config.slug} />
          </div>
        </main>

        {/* Footer overlays the illustration's purple band at the bottom
            rather than sitting in its own paper-coloured strip. The
            relative wrapper positions it absolutely over the SVG. */}
        <div className="relative">
          <OnePagerIllustration src={config.illustration.src} />
          <div className="absolute inset-x-0 bottom-0">
            <OnePagerFooter pdfDownloadPath={config.pdfDownloadPath} />
          </div>
        </div>
      </div>

      {/* Floating CTA is screen-only — fixed positioning doesn't print
          well, and the inline variant covers the PDF surface. */}
      <div className="print:hidden">
        <BookACallButton variant="floating" analyticsSlug={config.slug} />
      </div>
      <OnePagerMenu />
    </div>
  );
};

export default IndustryOnePagerPage;
